/**
 * Store Pinia des mouvements de stock (composition API)
 */
import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { IStockMovement } from "../models/stockMovement";
import { IStockMovementFilter } from "../models/filters/stockMovementFilter";
import StockMovementService from "../services/StockMovementService";
import { IStockMovementForm } from "../models/forms/stockMovementForm";
import { IStockMovementApiResponse } from "../models/api/stockMovementApiResponse";
import { ServiceOperationResult } from "../models/api/serviceOperationResult";
import DataOperationError from "../models/api/dataOperationError";
import { IDataOperationResult } from "../models/api/dataOperationResult";
import { ICenter } from "../models/center";
import { IMovementType } from "../models/movementType";
import { IStockUnity } from "../models/stockUnity";
import { IArticleStockUnityMovementForm } from "../models/forms/articleForm";
import { GroupedStock } from "../helper/type";
import { useCenterStore } from "./center.store";
import { useStockUnityStore } from "./stockUnity.store";
import { useMovementTypeStore } from "./movementType.store";
import { useBoars } from "./boars.store";
import StockUnityService from "../services/StockUnityService";
import BoarService from "../services/BoarService";
import { IBoarApiResponse } from "../models/api/boarApiResponse";
import signalRManager from "../helper/signalRManager";
import IPayloadStore from "../models/api/payloadStore";
import i18n from "@/i18n";
import { IStockUnityApiResponse } from "@/models/api/stockUnityApiResponse";

export const useStockMovementStore = defineStore("stockmovements", () => {
    // State
    const datas = ref<IStockMovement[]>([]);
    const current = ref<IStockMovement>({} as IStockMovement);
    const itemForm = ref<IStockMovementForm>({} as IStockMovementForm);
    const isLoading = ref<boolean>(false);

    // Getters
    // 0028451 — La nature de l'article se lit sur son drapeau `isMaterial`, JAMAIS sur la forme de
    // son code. La règle précédente (« code numérique = matériel ») laissait passer en semence tout
    // article matériel au code alphanumérique — 63 articles sur la base de recette. Même lecture que
    // StockConsultation, StockAdjustments, StockDestroy, StockHistorique et StockTransfers.
    const isSemence = (mouvement: IStockMovement) =>
        mouvement.unity.article?.isMaterial !== true && !!mouvement.unity.article?.code;

    const getMouvementSemence = computed(() => () => {
        return datas.value.filter(isSemence);
    });

    const getMouvementSemenceArticle = computed(() => (idArticle: number, stockGroupSelected: GroupedStock) => {
        return datas.value.filter((mouvement) =>
            isSemence(mouvement) &&
            mouvement.unity.article.id == idArticle &&
            mouvement.unity.volumeDose == stockGroupSelected.volumeDose &&
            mouvement.unity.spzDose == stockGroupSelected.spzDose &&
            Boolean(mouvement.unity.heterospermie) == stockGroupSelected.isHeterospermie &&
            mouvement.unity.conditionnement?.code3 == stockGroupSelected.conditionnementCode &&
            mouvement.unity.isArmada == stockGroupSelected.isArmada
        );
    });

    const getMouvementSemenceSchema = computed(() => (codeSchema: string, stockGroupSelected: GroupedStock) => {
        return datas.value.filter((mouvement) =>
            isSemence(mouvement) &&
            mouvement.unity.article?.geneticType?.code3 == codeSchema &&
            mouvement.unity.volumeDose == stockGroupSelected.volumeDose &&
            mouvement.unity.spzDose == stockGroupSelected.spzDose &&
            Boolean(mouvement.unity.heterospermie) == stockGroupSelected.isHeterospermie &&
            mouvement.unity.conditionnement?.code3 == stockGroupSelected.conditionnementCode &&
            mouvement.unity.isArmada == stockGroupSelected.isArmada
        );
    });

    // Actions - Hydration
    /**
     * Hydrate une liste de mouvements de stock en batch (optimisé pour la performance)
     * - Charge centerStore, stockUnityStore et movementTypeStore si nécessaires
     * - Crée des Maps d'index pour des lookups O(1)
     */
    async function hydrateStockMovements(items: IStockMovementApiResponse[]): Promise<IStockMovement[]> {
        if (items.length === 0) return [];

        // 1. Récupérer les stores dépendants
        const centerStore = useCenterStore();
        const stockUnityStore = useStockUnityStore();
        const movementTypeStore = useMovementTypeStore();
        const boarsStore = useBoars();

        // 2. Charger les stores dépendants en parallèle si nécessaire
        const loadPromises: Promise<unknown>[] = [];

        if (centerStore.datas.length === 0) {
            loadPromises.push(centerStore.load({ page: 1, pageSize: 0 }));
        }
        if (stockUnityStore.datas.length === 0) {
            loadPromises.push(stockUnityStore.load({ page: 1, pageSize: 0 }));
        }
        if (movementTypeStore.datas.length === 0) {
            loadPromises.push(movementTypeStore.load({ page: 1, pageSize: 0 }));
        }

        if (loadPromises.length > 0) {
            await Promise.all(loadPromises);
        }

        // 3. Créer des Maps pour des lookups O(1)
        const centerMap = new Map(centerStore.datas.map(c => [c.id, c]));
        const stockUnityMap = new Map(stockUnityStore.datas.map(u => [u.id, u]));
        const movementTypeMap = new Map(movementTypeStore.datas.map(m => [m.id, m]));
        const boarMap = new Map(boarsStore.datas.map(b => [b.id, b]));

        // 4. Fetch on-demand des unités manquantes (inactives/consommées, absentes car Active=true au chargement)
        //    getById bypasse le filtre Active — retourne l'unité quel que soit son statut
        const missingUnityIds = [...new Set(
            items.map(i => i.unityId).filter(id => !stockUnityMap.has(id))
        )];
        if (missingUnityIds.length > 0) {
            const responses = await Promise.all(
                missingUnityIds.map(id => StockUnityService.getById<IStockUnityApiResponse>(id))
            );
            const validItems = responses
                .filter(r => !r.error && r.data != null)
                .map(r => r.data as IStockUnityApiResponse);
            if (validItems.length > 0) {
                const hydrated = await stockUnityStore.hydrateStockUnities(validItems);
                hydrated.forEach(u => stockUnityMap.set(u.id, u));
            }
        }

        // 5. Fetch on-demand des verrats manquants (cross-centre, absents de boarsStore)
        //    getById bypasse les filtres isPresent/idCenter — retourne le verrat quel que soit son centre
        //    On se limite aux unités référencées par les items à hydrater (pas tout le stockUnityStore)
        const missingBoarIds = [...new Set(
            items
                .map(i => stockUnityMap.get(i.unityId))
                .filter((u): u is NonNullable<typeof u> => !!u)
                .map(u => (u as any).boarId as number | undefined)
                .filter((id): id is number => !!id && !boarMap.has(id))
        )];
        if (missingBoarIds.length > 0) {
            const responses = await Promise.all(
                missingBoarIds.map(id => BoarService.getById<IBoarApiResponse>(id))
            );
            responses
                .filter(r => !r.error && r.data != null)
                .forEach(r => boarMap.set((r.data as IBoarApiResponse).id, r.data as any));
        }

        // 6. Hydrater tous les mouvements en une seule passe
        const models: IStockMovement[] = [];
        for (const item of items) {
            const model = { ...item } as unknown as IStockMovement;

            model.center = centerMap.get(item.centerId)!;
            model.unity = stockUnityMap.get(item.unityId)!;
            model.movementType = movementTypeMap.get(item.movementTypeId)!;

            // Patch boar si l'unité existe mais que le verrat n'était pas dans boarsStore à l'hydratation
            if (model.unity && !model.unity.boar) {
                const boarId = (model.unity as any).boarId as number | undefined;
                if (boarId) {
                    const boar = boarMap.get(boarId);
                    if (boar) model.unity = { ...model.unity, boar };
                }
            }

            models.push(model);
        }

        return models;
    }

    // Actions

    /**
     * Chargement des données dans le store
     */
    async function load(filter: IStockMovementFilter) {
        isLoading.value = true;
        const result = await StockMovementService.getAll<IStockMovementFilter, IStockMovementApiResponse>(filter);
        datas.value = [];

        if (!result.error) {
            datas.value = await hydrateStockMovements(result.data as IStockMovementApiResponse[]);
        }

        isLoading.value = false;
        return result;
    }

    /**
     * Récupération d'un mouvement de stock par son identifiant
     */
    async function get(id: number) {
        const result = await StockMovementService.getById<IStockMovementApiResponse>(id);

        if (!result.error) {
            const [hydratedItem] = await hydrateStockMovements([result.data as IStockMovementApiResponse]);

            const index = datas.value.findIndex((a) => a.id == id);
            if (index >= 0)
                datas.value[index] = hydratedItem;

            current.value = hydratedItem;
            itemForm.value = toForm(hydratedItem);
        }

        return result;
    }

    /**
     * Récupération de l'objet dans le store
     */
    async function searchStoreById(id: number): Promise<IStockMovement> {
        current.value = datas.value.find((objet) => objet.id == id)!;
        itemForm.value = toForm(current.value as IStockMovement);
        return current.value;
    }

    /**
     * Mise à jour ou création d'un objet
     */
    async function save(maj: boolean = true, isPatch: boolean = false) {
        let result = {} as ServiceOperationResult<IStockMovement | IDataOperationResult<IStockMovement>>;

        if (itemForm.value.id > 0) {
            result = await StockMovementService.update(itemForm.value);

            if (!result.error) {
                const [hydratedItem] = await hydrateStockMovements([result.data as unknown as IStockMovementApiResponse]);
                current.value = hydratedItem;

                if (maj) {
                    const index = datas.value.findIndex((a) => a.id == itemForm.value.id);
                    if (index >= 0) {
                        datas.value[index] = current.value;
                    } else {
                        const error = new DataOperationError();
                        error.REQUEST_ERROR = i18n.global.t("errors.itemNotFoundInStore");
                        return new ServiceOperationResult<IDataOperationResult<IStockMovement>>(true, { errors: error });
                    }
                }
            }
        } else {
            result = await StockMovementService.create(itemForm.value);

            if (!result.error) {
                const [hydratedItem] = await hydrateStockMovements([result.data as unknown as IStockMovementApiResponse]);
                current.value = hydratedItem;
                if (datas.value.findIndex((a) => a.id == current.value?.id) == -1) {
                    datas.value.push(current.value);
                }
            }
        }

        return result;
    }

    /**
     * Insertion manuelle d'une dose via la modale d'entrée stock.
     * Le payload est Code-based (article potentiellement nouveau, lot saisi à la main).
     */
    async function saveStockForm(form: IArticleStockUnityMovementForm) {
        if (form.id > 0) {
            return {} as ServiceOperationResult<IDataOperationResult<IStockMovement>>;
        }
        return StockMovementService.stockMovement(form);
    }

    /**
     * Annulation d'un mouvement de stock (REQ689–REQ700, REQ823).
     * Crée un mouvement ANN inversant l'effet sur le stock.
     */
    async function cancel(movementId: number, agentId?: number, commentaire?: string) {
        return StockMovementService.cancelMovement(movementId, agentId, commentaire);
    }

    /**
     * Delete de l'objet
     */
    async function deleteAction() {
        const result = await StockMovementService.delete<IStockMovementApiResponse>(current.value.id);

        if (!result.error) {
            const index = datas.value.findIndex((a) => a.id == current.value.id);
            if (index >= 0) {
                datas.value.splice(index, 1);
            }

            current.value = newItem({ id: 0, name: "", code: "", isActive: true } as ICenter);
            itemForm.value = newForm(0);
        }

        return result;
    }

    /**
     * Transformation de l'objet en formulaire
     */
    function toForm(item: IStockMovement): IStockMovementForm {
        const form = {} as IStockMovementForm;
        form.id = item.id;
        form.codeStockId = item.codeStockId;
        form.centerId = item.center.id;
        form.quantity = item.quantity;
        form.unityId = item.unity.id;
        form.movementTypeId = item.movementType.id;
        form.movementDate = item.movementDate;
        form.movementUser = item.movementUser;
        form.workStation = item.workStation;
        form.motifAjustementId = item.motifAjustementId;
        form.commentaire = item.commentaire;
        form.movementParentId = item.movementParentId;
        itemForm.value = form;
        return form;
    }

    /**
     * Permet d'initialiser un objet vide pour le formulaire
     */
    function newForm(lienId: number): IStockMovementForm {
        itemForm.value = {
            id: 0,
            centerId: lienId,
            codeStockId: 0,
            quantity: 0,
            unityId: 0,
            movementTypeId: 0,
            movementDate: new Date(),
            movementUser: "",
            workStation: "",
        } as IStockMovementForm;
        return itemForm.value;
    }

    /**
     * Permet d'initialiser un objet vide pour les referentiels
     */
    function newItem(lien: ICenter): IStockMovement {
        current.value = {
            id: 0,
            codeStockId: 0,
            quantity: 0,
            center: lien,
            movementDate: new Date(),
            movementUser: "",
            workStation: "",
            unity: { id: 0 } as IStockUnity,
            movementType: { id: 0 } as IMovementType,
        } as IStockMovement;
        itemForm.value = toForm(current.value);
        return current.value;
    }

    return {
        // State
        datas,
        current,
        itemForm,
        isLoading,
        // Getters
        getMouvementSemence,
        getMouvementSemenceArticle,
        getMouvementSemenceSchema,
        // Actions
        hydrateStockMovements,
        load,
        get,
        searchStoreById,
        save,
        saveStockForm,
        cancel,
        delete: deleteAction,
        toForm,
        newForm,
        newItem,
    };
});

signalRManager.connection.on("MajStockMovement", async (payload: IPayloadStore) => {
    const store = useStockMovementStore();
    switch (payload.action) {
        case "Create": {
            if (store.datas.findIndex((a) => a.id == payload.data?.id) == -1) {
                const [hydrated] = await store.hydrateStockMovements([payload.data as IStockMovementApiResponse]);
                store.datas.push(hydrated);
            }
            break;
        }
        case "Update": {
            const idx = store.datas.findIndex((a) => a.id == payload.data?.id);
            if (idx > -1) {
                const [hydrated] = await store.hydrateStockMovements([payload.data as IStockMovementApiResponse]);
                store.datas.splice(idx, 1, hydrated);
            }
            break;
        }
        case "Delete": {
            const idx = store.datas.findIndex((a) => a.id == payload.id);
            if (idx > -1) store.datas.splice(idx, 1);
            break;
        }
    }
});
